// components/ElasticSearch/ElasticSearch.jsx
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import Result from './Result';
import Pagination from './Pagination';
import SortButton from './SortButton';
import './Elasticsearch.scss'

const ElasticSearch = React.forwardRef(({
    payeeName,
    payerSource,
    address2,
    pageSize = 20,
    onResultsFound = () => { },
    onError = () => { }
}, ref) => {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalHits, setTotalHits] = useState(0);
    const [error, setError] = useState(null);
    const [searchAttempted, setSearchAttempted] = useState(false);
    const [sortConfig, setSortConfig] = useState(null);
    const [previousTerms, setPreviousTerms] = useState({
        payeeName: undefined,
        payerSource: undefined,
        address2: undefined,
        page: 1
    });

    const handlePageChange = (newPage) => {
        performSearch(newPage, sortConfig);
    };
    const handleSortChange = (newSortConfig) => {
        console.log(newSortConfig);
        setSortConfig(newSortConfig);
        performSearch(1, newSortConfig);
    };



    const termsCheck = (payeeName, payerSource, address2, currentPage, newSortConfig) => {
        const hasSearchTerm = (payeeName || payerSource || address2)
        // Check if search terms have changed
        const searchTermsChanged =
            (payeeName !== previousTerms.payeeName ||
                payerSource !== previousTerms.payerSource ||
                address2 !== previousTerms.address2);

        // Check if page has changed
        const pageChanged = currentPage !== previousTerms.page;

        const sortChanged = newSortConfig !== previousTerms.sortConfig;

        // Return true if either search terms or page has changed
        return hasSearchTerm && (searchTermsChanged || pageChanged || sortChanged);
    };
    const clearSearch = async () => {
        setPreviousTerms({
            payeeName: undefined,
            payerSource: undefined,
            address2: undefined,
            page: 1
        });
        setResults([]);
        setTotalHits(0);
        onResultsFound(0);
        setLoading(false);
        setError(null);
    }
    const performSearch = async (page = 1, newSortConfig = sortConfig) => {
        setSearchAttempted(true);
        // Only fetch if at least one search parameter is provided and terms have changed
        if (!termsCheck(payeeName, payerSource, address2, page, newSortConfig)) {
            return;
        }
        setPreviousTerms({ payeeName, payerSource, address2, page });
        setLoading(true);
        setError(null);

        try {
            // Build query based on provided parameters
            const mustClauses = [];

            if (payeeName) {
                mustClauses.push({
                    match: {
                        payee_name: payeeName
                    }
                });
            }

            if (payerSource) {
                mustClauses.push({
                    match: {
                        "payer_/_source": payerSource
                    }
                });
            }

            if (address2) {
                mustClauses.push({
                    match: {
                        address_2: address2
                    }
                });
            }

            const query = {
                from: ((page) - 1) * pageSize,
                size: pageSize,
                query: {
                    bool: {
                        must: mustClauses.length > 0 ? mustClauses : [{ match_all: {} }]
                    }
                }
            };
            // Add sort if sortConfig is set
            if (newSortConfig && newSortConfig.field) {
                query.sort = [
                    {
                        [newSortConfig.field]: {
                            order: newSortConfig.order || 'asc'
                        }
                    }
                ];
                console.log(query.sort);
            }

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL_BASE}/_search`,
                query,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            setResults(response.data.hits.hits.map(hit => hit._source));
            setTotalHits(response.data.hits.total.value);
            onResultsFound(response.data.hits.total.value);
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'An error occurred while searching';
            setError(errorMessage);
            onError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Expose the performSearch method to parent components
    useImperativeHandle(ref, () => ({
        performSearch,
        clearSearch
    }));

    if (loading) {
        return <div role="status" aria-live="polite">Loading results...</div>;
    }

    if (error) {
        return (
            <div role="alert" className="error-message">
                Error: {error}
            </div>
        );
    }

    return (
        <div>
            <div className="results-summary" aria-live="polite">
                {(() => {
                    if (totalHits > 0) {
                        return (
                            <p className='pager-summary'>Showing {(previousTerms.page-1)*pageSize+1} &mdash; {(previousTerms.page)*pageSize} of {totalHits} results</p>
                        )
                    } else if (previousTerms.payeeName || previousTerms.payerSource || previousTerms.address2) {
                        return (
                            <div>
                                <p>No results matched your search for '{[previousTerms.payeeName, previousTerms.payerSource, previousTerms.address2].filter(Boolean).join(", ")}'. Please broaden your search and try again.</p>
                                <p>Department of Treasury and Finance only maintains a register of Western Australian organisations.</p>
                                <p>Should you wish to search other sources of unclaimed money, please refer to <a target='_blank' href="https://www.moneysmart.gov.au/tools-and-resources/find-unclaimed-money">ASIC's MoneySmart website.</a></p>
                            </div>
                        )
                    } else if(searchAttempted){
                        return (
                            <p>No results returned. Please enter Payee name and suburb name.</p>
                        )
                    } else {
                        return (
                            <p></p>
                        )
                    }
                })()}
            </div>
            {(totalHits > 0) && (
                <div>
                    <div className="results-header">
                        <div className="header-cell name">
                            <SortButton
                                label="Payee name"
                                field="payee_name"
                                sortConfig={sortConfig}
                                onSortChange={handleSortChange}
                            />
                        </div>
                        <div className="header-cell amount">

                            <SortButton
                                label="Amount"
                                field="amount_unclaimed"
                                sortConfig={sortConfig}
                                onSortChange={handleSortChange}
                            />
                        </div>
                        <div className="header-cell suburb">

                            <SortButton
                                label="Payee suburb"
                                field="address_2"
                                sortConfig={sortConfig}
                                onSortChange={handleSortChange}
                            />
                        </div>
                        <div className="header-cell source">
                            Claim from
                        </div>
                        <div className="header-cell actions">
                            {/* This is intentionally empty to help space out the header and cells consistently */}
                            &nbsp;
                        </div>
                    </div>
                    <div className="results-list">
                        {results.map((hit, index) => (
                            <Result hit={hit} />
                        ))}
                    </div>
                    <Pagination
                        totalItems={totalHits}
                        itemsPerPage={pageSize}
                        currentPage={previousTerms.page}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}

        </div>
    );
});

export default ElasticSearch;
